/**
 * AB#1754 — Calendar settings TanStack Query hook (Calendar v1, C8).
 *
 * Single query + single atomic mutation. The BE exposes ONE `PUT /api/calendar/settings`
 * endpoint that replaces both the settings row and all 7 status rows in a single
 * transaction — there is no per-status PUT. This hook mirrors that contract.
 *
 * The locked-status invariant (Booked + Completed cannot be disabled) is enforced
 * server-side; the FE derives `isLocked` client-side from `LOCKED_STATUS_KEYS`
 * and renders the toggle as disabled to fail fast.
 *
 * Hardcoded EN strings per the deferred-i18n-batch convention across
 * Calendar v1 (CR-2026-05-12-v2, AB#1713). AR translations land in the batch PBI.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import type {
    CalendarSettingsResponse,
    UpdateCalendarSettingsRequest,
} from '../types/api-responses';

/**
 * GET /api/calendar/settings — fetches the current tenant's calendar settings.
 * Tenant scope is implicit (resolved from JWT). 60s stale to keep the modal
 * snappy on re-opens but still fresh enough for cross-tab edits to land.
 */
export const useCalendarSettingsQuery = () =>
    useQuery<CalendarSettingsResponse>({
        queryKey: queryKeys.calendar.settings(),
        queryFn: () => apiService.getCalendarSettings(),
        staleTime: 60_000,
    });

/**
 * PUT /api/calendar/settings — atomic replace.
 * On success, invalidate the full `calendar.all` namespace so any open
 * calendar grid / availability picker / settings page re-fetches with
 * the new palette + slot granularity.
 */
export const useUpdateCalendarSettingsMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<CalendarSettingsResponse, Error, UpdateCalendarSettingsRequest>({
        mutationFn: (body) => apiService.updateCalendarSettings(body),
        onSuccess: (fresh) => {
            // Surgical update — write the fresh snapshot into the cache so
            // the form doesn't flash a loading state between the mutation
            // and the invalidate-driven refetch.
            queryClient.setQueryData(queryKeys.calendar.settings(), fresh);
            queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
        },
    });
};
